import React, {useCallback, useContext, useMemo} from "react";
import PurchaseCostCoveragePanel from "../../pages/dashboard/OrderDashboard/PurchaseCostCoveragePanel.jsx";
import {hasPurchaseCostCoverageContext, shouldHidePurchaseCostCoverage} from "../../utils/purchaseCostCoveragePresentation.js";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";

export function buildCoverageApiRequest(projectId, params, orderId = null) {
    return {
        projectId,
        ...(orderId ? {orderId} : {}),
        page: params.page,
        pageSize: params.pageSize,
        reportContext: params.reportContext ?? "marketing",
        ...(params.scopeId ? {scopeId: params.scopeId} : {}),
        ...(params.productMode ? {productMode: params.productMode} : {}),
        erpFilter: {
            since: params.since,
            until: params.until,
            selectedCentreIds: params.selectedCentreIds,
            selectedDomainIds: params.selectedDomainIds,
        },
    };
}

function CoveragePanelEntry({
    data,
    loading,
    reportContext,
    scopeId,
    productMode,
    requestFilters,
    additionalHelp,
    periodIdentity,
    loadOrders,
    loadOrderItems,
    requestEnabled,
    comparisonLayout,
}) {
    const globalContext = useContext(GlobalContext) ?? {};
    const currentProjectId = globalContext.context?.currentProjectId;
    const getAuthorityConfiguration = globalContext.getAuthorityConfiguration;
    const api = useMemo(() => currentProjectId && getAuthorityConfiguration ? new ErpCalculationApi(getAuthorityConfiguration()) : null, [currentProjectId, getAuthorityConfiguration]);
    const generatedOrders = useCallback((params) => api?.purchaseCostCoverage(buildCoverageApiRequest(currentProjectId, params)), [api, currentProjectId]);
    const generatedItems = useCallback((params) => api?.purchaseCostCoverageOrderItems(buildCoverageApiRequest(currentProjectId, params, params.orderId)), [api, currentProjectId]);
    const scopedOrders = useCallback((params) => (loadOrders ?? generatedOrders)({
        ...params,
        reportContext,
        ...(scopeId ? {scopeId} : {}),
        ...(productMode ? {productMode} : {}),
    }), [loadOrders, generatedOrders, reportContext, scopeId, productMode]);
    const scopedItems = useCallback((params) => (loadOrderItems ?? generatedItems)({
        ...params,
        reportContext,
        ...(scopeId ? {scopeId} : {}),
        ...(productMode ? {productMode} : {}),
    }), [loadOrderItems, generatedItems, reportContext, scopeId, productMode]);
    const requestScopeKey = [currentProjectId ?? "", reportContext, scopeId ?? "", productMode ?? "", periodIdentity ?? ""].join(":");
    const coverageBasis = reportContext === "marketing" || reportContext === "campaigns" || reportContext === "marketing_trends" || (reportContext === "product_profitability" && productMode === "gross")
        ? "non_cancelled"
        : "completed";

    return <PurchaseCostCoveragePanel
        coveragePercent={data?.revenueCoveragePercent}
        knownLineCount={data?.knownLineCount}
        unknownLineCount={data?.unknownLineCount}
        actionableUnknownLineCount={data?.actionableUnknownLineCount}
        estimatedLineCount={data?.estimatedLineCount}
        additionalHelp={additionalHelp}
        coverageBasis={coverageBasis}
        loading={loading}
        loadOrders={currentProjectId && requestEnabled ? scopedOrders : undefined}
        loadOrderItems={currentProjectId && requestEnabled ? scopedItems : undefined}
        requestFilters={requestFilters}
        requestScopeKey={requestScopeKey}
        comparisonLayout={comparisonLayout}
    />;
}

/** Shared blue order-detail panel for report-wide purchase-cost coverage. */
export default function ReportingCostCoverageContext({
    data,
    periods,
    loading = false,
    reportContext = "marketing",
    scopeId,
    productMode,
    requestFilters,
    additionalHelp,
    loadOrders,
    loadOrderItems,
    requestEnabled = true,
    comparisonLayout = false,
}) {
    const entries = (periods ?? [{label: null, data, requestFilters}])
        .filter((entry) => hasPurchaseCostCoverageContext(entry.data))
        .filter((entry) => !shouldHidePurchaseCostCoverage(entry.data?.revenueCoveragePercent, entry.data?.actionableUnknownLineCount));
    if (entries.length === 0) return null;
    return <>
        {entries.map((entry, index) => {
            const entryContext = entry.reportContext ?? reportContext;
            const identity = entry.periodIdentity ?? entry.label ?? index;
            const columnSpan = entries.length === 1 ? "col-span-12" : entries.length === 2 ? "col-span-12 lg:col-span-6" : comparisonLayout ? "col-span-12 lg:col-span-4" : "col-span-12 lg:col-span-6 xl:col-span-4";
            return <div key={`${entryContext}:${identity}`} className={comparisonLayout ? "col-span-12 flex flex-1 flex-col" : columnSpan}>
                {entry.label && <h3 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">{entry.label}</h3>}
                <CoveragePanelEntry
                    data={entry.data}
                    loading={loading}
                    reportContext={entryContext}
                    scopeId={entry.scopeId ?? scopeId}
                    productMode={entry.productMode ?? productMode}
                    requestFilters={entry.requestFilters ?? requestFilters}
                    additionalHelp={entry.additionalHelp ?? additionalHelp}
                    periodIdentity={identity}
                    loadOrders={loadOrders}
                    loadOrderItems={loadOrderItems}
                    requestEnabled={requestEnabled}
                    comparisonLayout={comparisonLayout}
                />
            </div>;
        })}
    </>;
}
